/**
 * Typed event bus for auth state changes, with BroadcastChannel
 * cross-tab synchronization.
 *
 * Local subscribers receive every state transition via `notify()`. Cross-tab
 * events (`auth_changed`/`auth_logout`) are explicit broadcasts sent only on
 * login/logout so other tabs can re-read shared storage. Per-transition
 * `state_changed` events are NOT broadcast — the originating tab already
 * wrote shared storage and the receiving tab ignores them, so broadcasting
 * would only ship tokens over the channel for no benefit.
 */

import { createSecureRandomId } from "./random";
import type { AuthState, StateListener, StorageAdapter, Unsubscribe } from "./types";

const MAX_STORAGE_EVENT_BYTES = 1_024;

export type AuthEvent =
  | { type: "auth_changed"; epoch: string; revision: number }
  | { type: "auth_logout"; epoch: string };

export class EventBus {
  private listeners = new Set<StateListener>();
  private channel: BroadcastChannel | null = null;
  private readonly channelName: string;
  private readonly sharing: StorageAdapter["sharing"];
  private storage: Storage | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private storageOwner: string | null = null;
  private storageSequence = 0;
  private remoteHandler: ((event: AuthEvent) => void) | null = null;

  constructor(namespace: string, sharing: StorageAdapter["sharing"]) {
    this.channelName = `zero_${namespace}_auth`;
    this.sharing = sharing;
    if (sharing === "same-origin") this.initCrossContextChannels();
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: StateListener): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Notify local subscribers. Does not broadcast — use `broadcast()` for cross-tab events. */
  notify(state: AuthState): void {
    for (const listener of this.listeners) {
      listener(state);
    }
  }

  /** Broadcast an auth event to other tabs. */
  broadcast(event: AuthEvent): void {
    if (this.channel) {
      try {
        this.channel.postMessage(event);
        return;
      } catch {
        // Fall through to the storage-event channel established at construction.
      }
    }
    if (this.storage && this.storageOwner) {
      this.storageSequence += 1;
      const envelope = JSON.stringify({
        owner: this.storageOwner,
        sequence: this.storageSequence,
        event,
      });
      if (envelope.length > MAX_STORAGE_EVENT_BYTES) {
        throw new RangeError("Auth event exceeds storage-event capacity");
      }
      this.storage.setItem(`${this.channelName}_event`, envelope);
      return;
    }
    if (this.sharing === "same-origin") {
      throw new Error("Cross-context auth event channel is unavailable");
    }
  }

  /** Set a handler for incoming cross-tab events. */
  onRemoteMessage(handler: (event: AuthEvent) => void): void {
    this.remoteHandler = handler;
    if (this.channel) {
      this.channel.onmessage = (e: MessageEvent<AuthEvent>) => {
        if (isAuthEvent(e.data)) {
          handler(e.data);
        }
      };
    }
  }

  /** Tear down the channel and clear all listeners. */
  destroy(): void {
    this.listeners.clear();
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = null;
    }
    if (this.storageListener && typeof window !== "undefined") {
      window.removeEventListener("storage", this.storageListener);
    }
    this.storage = null;
    this.storageListener = null;
    this.storageOwner = null;
    this.remoteHandler = null;
  }

  private initCrossContextChannels(): void {
    if (typeof BroadcastChannel === "function") {
      try {
        this.channel = new BroadcastChannel(this.channelName);
      } catch {
        this.channel = null;
      }
    }
    this.initStorageFallback();
    if (!this.channel && !this.storage) {
      throw new Error("Same-origin auth storage requires BroadcastChannel or localStorage events");
    }
  }

  private initStorageFallback(): void {
    if (typeof window === "undefined") return;
    try {
      const storage = window.localStorage;
      const probeKey = `${this.channelName}_probe`;
      storage.setItem(probeKey, "1");
      storage.removeItem(probeKey);
      this.storage = storage;
      this.storageOwner = createSecureRandomId("auth-event-");
      this.storageListener = (event: StorageEvent) => {
        if (event.key !== `${this.channelName}_event` || event.newValue == null) return;
        const authEvent = parseStorageEvent(event.newValue);
        if (authEvent) this.remoteHandler?.(authEvent);
      };
      window.addEventListener("storage", this.storageListener);
    } catch {
      this.storage = null;
      this.storageOwner = null;
      this.storageListener = null;
    }
  }
}

function parseStorageEvent(value: string): AuthEvent | null {
  if (value.length === 0 || value.length > MAX_STORAGE_EVENT_BYTES) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null || !("event" in parsed)) return null;
    return isAuthEvent(parsed.event) ? parsed.event : null;
  } catch {
    return null;
  }
}

function isAuthEvent(value: unknown): value is AuthEvent {
  if (typeof value !== "object" || value === null || !("type" in value)) return false;
  if (value.type === "auth_changed") {
    return (
      "epoch" in value &&
      isValidEpoch(value.epoch) &&
      "revision" in value &&
      typeof value.revision === "number" &&
      Number.isSafeInteger(value.revision) &&
      value.revision > 0
    );
  }
  if (value.type !== "auth_logout" || !("epoch" in value)) return false;
  return isValidEpoch(value.epoch);
}

function isValidEpoch(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}
